import React, { useState } from "react";
import i18n from "../../../../i18n";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import {
  useGetSeriesAppointmentsQuery,
  useExtendSeriesMutation,
  usePatchAppointmentMutation,
  useGetProgramSessionsQuery,
  useAddProgramSessionsMutation,
  useDeleteProgramSessionMutation,
} from "../../../../services/api/utils.api";
import deleteIcon from "../../../../images/delete_icon.svg";
import styles from "./SeriesManager.module.css";

interface ISeriesManagerProps {
  /** Legacy series linkage (patient-less / old data). */
  seriesId?: string;
  /** Program linkage — when present, the component is program-driven (preferred). */
  appointmentsProgramsId?: number;
  currentAppointmentId: number;
  onSuccess: () => void;
}

interface INormalizedSession {
  id: number;
  appointmentDate: string | null;
  statusId: number | null;
}

function isPast(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  return d < todayStart;
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

const SeriesManager: React.FC<ISeriesManagerProps> = ({
  seriesId,
  appointmentsProgramsId,
  currentAppointmentId,
  onSuccess,
}) => {
  const isRtl = i18n.language === "he";

  // Program linkage is the source of truth when present; otherwise fall back to
  // the legacy seriesId mechanism (patient-less / old data).
  const isProgramMode = appointmentsProgramsId != null;

  // ========== DATA FETCHING ==========
  const { data: seriesAppointments = [], refetch: refetchSeries, isLoading: isLoadingSeries } =
    useGetSeriesAppointmentsQuery(seriesId ?? "", {
      skip: isProgramMode || !seriesId,
    });
  const { data: programData, refetch: refetchProgram, isLoading: isLoadingProgram } =
    useGetProgramSessionsQuery(appointmentsProgramsId ?? 0, {
      skip: !isProgramMode,
    });

  const [extendSeries, { isLoading: isExtendingSeries }] = useExtendSeriesMutation();
  const [patchAppointment, { isLoading: isPatching }] = usePatchAppointmentMutation();
  const [addProgramSessions, { isLoading: isAddingProgram }] = useAddProgramSessionsMutation();
  const [deleteProgramSession, { isLoading: isDeletingProgram }] = useDeleteProgramSessionMutation();

  const isLoading = isProgramMode ? isLoadingProgram : isLoadingSeries;
  const isExtending = isProgramMode ? isAddingProgram : isExtendingSeries;

  // Normalize both sources to a single shape the UI renders.
  const sessions: INormalizedSession[] = isProgramMode
    ? (programData?.sessions ?? []).map((s) => ({
        id: s.id,
        appointmentDate: s.appointmentDate,
        statusId: s.statusId,
      }))
    : seriesAppointments.map((a) => ({
        id: a.id,
        appointmentDate: a.appointmentDate,
        statusId: a.statusId,
      }));

  const refetch = isProgramMode ? refetchProgram : refetchSeries;

  // ========== STATE ==========
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showExtendPicker, setShowExtendPicker] = useState(false);

  // ========== COMPUTED VALUES ==========
  const activeAppointments = sessions.filter((a) => a.statusId !== 0);

  // ========== EVENT HANDLERS ==========
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isDeleting = isProgramMode ? isDeletingProgram : isPatching;

  const handleSoftDelete = async () => {
    if (selectedIds.size === 0 || isDeleting) return;
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          isProgramMode
            ? deleteProgramSession({
                programId: appointmentsProgramsId!,
                appointmentId: id,
              }).unwrap()
            : patchAppointment({ id, statusId: 0 }).unwrap(),
        ),
      );
      setSelectedIds(new Set());
      refetch();
      onSuccess();
    } catch {
      // silent — individual failures don't block others
    }
  };

  const handleExtend = async (count: number) => {
    setShowExtendPicker(false);
    try {
      if (isProgramMode) {
        await addProgramSessions({ programId: appointmentsProgramsId!, count }).unwrap();
      } else if (seriesId) {
        await extendSeries({ seriesId, count }).unwrap();
      }
      refetch();
      onSuccess();
    } catch {
      // silent
    }
  };

  // ========== RENDER ==========
  if (isLoading) {
    return (
      <div className={styles.loading} dir={isRtl ? "rtl" : "ltr"}>
        <span className={styles.spinner} />
      </div>
    );
  }

  return (
    <div className={styles.container} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.pills}>
        {activeAppointments.map((appt) => {
          const past = isPast(appt.appointmentDate);
          const isCurrent = appt.id === currentAppointmentId;
          const isSelected = selectedIds.has(appt.id);

          return (
            <button
              key={appt.id}
              type="button"
              disabled={past}
              onClick={() => !past && toggleSelect(appt.id)}
              className={[
                styles.pill,
                past ? styles.pillPast : styles.pillFuture,
                isCurrent ? styles.pillCurrent : "",
                isSelected ? styles.pillSelected : "",
              ]
                .filter(Boolean)
                .join(" ")}
              title={appt.appointmentDate || ""}
            >
              {formatShortDate(appt.appointmentDate)}
            </button>
          );
        })}

        {/* Action buttons — trash and plus side by side at the end */}
        <div className={styles.actions}>
          <button
            type="button"
            className={[
              styles.actionBtn,
              styles.trashBtn,
              selectedIds.size > 0 ? styles.trashActive : styles.trashDisabled,
            ].join(" ")}
            disabled={selectedIds.size === 0 || isDeleting}
            onClick={handleSoftDelete}
            title={
              selectedIds.size > 0
                ? `מחק ${selectedIds.size} פגישות`
                : "בחר פגישות עתידיות למחיקה"
            }
          >
            <img src={deleteIcon} alt="מחק" className={styles.trashIcon} />
          </button>

          <div className={styles.extendWrapper}>
            <button
              type="button"
              className={[styles.actionBtn, styles.plusBtn].join(" ")}
              onClick={() => setShowExtendPicker((v) => !v)}
              disabled={isExtending}
              title="הוסף פגישות לסדרה"
            >
              {isExtending ? "⏳" : "+"}
            </button>

            {showExtendPicker && (
              <div className={styles.extendPicker}>
                <span className={styles.extendPickerLabel}>
                  {t("SERIES_MANAGEMENT_EXTEND_PICKER_LABEL") || "כמה פגישות?"}
                </span>
                <div className={styles.extendPickerNums}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={styles.extendNum}
                      onClick={() => handleExtend(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <p className={styles.selectionHint}>
          {selectedIds.size} פגישות נבחרו — לחץ 🗑️ למחיקה
        </p>
      )}
    </div>
  );
};

export default SeriesManager;
